import React from 'react';
import { Crown, PackageOpen, Tags, Lock } from 'lucide-react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import styles from './LoyaltyPrograms.module.css';

// 📂 Context Import
import { useAuth } from '../../../context/AuthContext';

// 📂 Flat Structure Imports
import MembershipManager from './components/MembershipManager/MembershipManager';
import PackagesManager from './components/PackagesManager/PackagesManager';

export default function LoyaltyPrograms() {
    const navigate = useNavigate();
    const location = useLocation();
    const { hasPermission, user } = useAuth();
    
    // 🚀 STRICT MASTER PERMISSIONS
    const isSuperAdmin = ['SUPERADMIN', 'ADMIN'].includes(user?.role);
    const canViewMemberships = isSuperAdmin || hasPermission('memberships', 'view');
    const canViewPackages = isSuperAdmin || hasPermission('packages', 'view');

    // Safe detection of active tab
    const isMembershipActive = location.pathname.includes('/manage-loyalty-program/membership');
    const isPackageActive = location.pathname.includes('/manage-loyalty-program/package');

    // 🚀 SMART ROUTING: Agar Membership band hai, toh sidha Packages par bhejo
    const defaultRoute = canViewMemberships 
        ? "/manage-loyalty-program/membership" 
        : (canViewPackages ? "/manage-loyalty-program/package" : null);

    // 🔴 COMPLETELY DENIED FALLBACK (Agar dono band hain)
    if (!canViewMemberships && !canViewPackages) {
        return (
            <main className={styles.page} style={{alignItems: 'center', justifyContent: 'center'}}>
                <div style={{textAlign: 'center', padding: '60px', background: '#fff', borderRadius: '12px', border: '1px dashed #fca5a5'}}>
                    <Lock size={40} color="#dc2626" style={{marginBottom: '16px'}}/>
                    <h2 style={{margin: '0 0 8px', fontSize: '20px', color: '#0f172a'}}>Access Denied</h2>
                    <p style={{margin: 0, fontSize: '13px', color: '#64748b'}}>You do not have permission to view Loyalty Programs.</p>
                </div>
            </main>
        );
    }

    return (
        <main className={styles.page}>
            <header className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <span className={styles.breadcrumb}>
                        MARKETING & SALES <span className={styles.slash}>/</span> LOYALTY PROGRAMS
                    </span>
                    <h1>Loyalty Programs</h1>
                    <p className={styles.headerSubtext}>
                        Manage memberships, prepaid wallets, and service packages.
                    </p>
                </div>
                <div className={styles.headerBadge}>
                    <span className={styles.headerBadgeDot} />
                    Program Centre
                </div>
            </header>

            <div className={styles.mainContainer}>
                <aside className={styles.sidebar}>
                    <div className={styles.sidebarLabel}>PROGRAMS</div>
                    <nav className={styles.navMenu}>
                        
                        {/* 🟢 MEMBERSHIPS TAB (Locks dynamically) */}
                        <button
                            type="button"
                            className={`${styles.navItem} ${isMembershipActive ? styles.activeNav : ''} ${!canViewMemberships ? styles.disabledNav : ''}`}
                            onClick={() => canViewMemberships && navigate('/manage-loyalty-program/membership')}
                            disabled={!canViewMemberships}
                            title={!canViewMemberships ? "Access Restricted by Admin" : ""}
                        >
                            <span className={styles.navItemIcon}>
                                {!canViewMemberships ? <Lock size={15} color="#94a3b8"/> : <Crown size={17} />}
                            </span>
                            <span className={styles.navItemText}>
                                <strong>Memberships</strong>
                                <small>Discount & prepaid plans</small>
                            </span>
                        </button>

                        {/* 🟢 PACKAGES TAB (Locks dynamically) */}
                        <button
                            type="button"
                            className={`${styles.navItem} ${isPackageActive ? styles.activeNav : ''} ${!canViewPackages ? styles.disabledNav : ''}`}
                            onClick={() => canViewPackages && navigate('/manage-loyalty-program/package')}
                            disabled={!canViewPackages}
                            title={!canViewPackages ? "Access Restricted by Admin" : ""}
                        >
                            <span className={styles.navItemIcon}>
                                {!canViewPackages ? <Lock size={15} color="#94a3b8"/> : <PackageOpen size={17} />}
                            </span>
                            <span className={styles.navItemText}>
                                <strong>Service Packages</strong>
                                <small>Bundle services and add-ons</small>
                            </span>
                        </button>

                        <div className={styles.divider} />

                        {/* 🔴 PROMO CODES TAB (COMING SOON) */}
                        <button type="button" className={`${styles.navItem} ${styles.disabledNav}`} disabled>
                            <span className={styles.navItemIcon}><Tags size={17} /></span>
                            <span className={styles.navItemText}>
                                <strong>Promo Codes</strong>
                                <small>Campaign discounts</small>
                            </span>
                            <span className={styles.comingSoon}>Soon</span>
                        </button>
                    </nav>

                    <div className={styles.sidebarFooter}>
                        <div className={styles.footerIcon}><Crown size={15} /></div>
                        <div>
                            <strong>Build customer loyalty</strong>
                            <span>Reward repeat visits with flexible plans.</span>
                        </div>
                    </div>
                </aside>

                <section className={styles.contentArea}>
                    <Routes>
                        {/* 🚀 Render routes ONLY if user has permission */}
                        {canViewMemberships && <Route path="membership/*" element={<MembershipManager />} />}
                        {canViewPackages && <Route path="package/*" element={<PackagesManager />} />}
                        
                        {/* 🚀 Smart Routing: Redirects to whatever is allowed */}
                        {defaultRoute && <Route path="*" element={<Navigate to={defaultRoute} replace />} />}
                    </Routes>
                </section>
            </div>
        </main>
    );
}